import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Upload, Download, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  "Will Create": "border-emerald-200 text-emerald-700 bg-emerald-50",
  "Will Update (existing lead)": "border-blue-200 text-blue-700 bg-blue-50",
  "Will Create New Taxonomy Value": "border-amber-200 text-amber-700 bg-amber-50",
  "Error": "border-red-200 text-red-700 bg-red-50",
};

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const objUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = objUrl; a.download = filename; a.click();
  URL.revokeObjectURL(objUrl);
}

export default function BulkImport() {
  const { user } = useAuth();
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const canAssets = roles.includes("admin");
  const canLeads = roles.includes("admin") || roles.includes("relationship_manager");

  const [mode, setMode] = useState(canAssets ? "assets" : "leads");
  const [step, setStep] = useState("upload"); // upload | mapping | preview | result
  const [parsed, setParsed] = useState(null); // {import_id, headers, target_fields, suggested_mapping, total_rows, sample_rows}
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setStep("upload"); setParsed(null); setMapping({}); setPreviewData(null); setResult(null); };
  const switchMode = (m) => { setMode(m); reset(); };

  const parseMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/bulk-import/${mode}/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      setStep("mapping");
      toast.success(`Parsed ${data.total_rows} row(s)`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Parse failed"),
  });

  const previewMut = useMutation({
    mutationFn: async () => (await api.post(`/bulk-import/${mode}/preview`, { import_id: parsed.import_id, mapping })).data,
    onSuccess: (data) => { setPreviewData(data); setStep("preview"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Preview failed"),
  });

  const commitMut = useMutation({
    mutationFn: async () => (await api.post(`/bulk-import/${mode}/commit`, { import_id: parsed.import_id, mapping })).data,
    onSuccess: (data) => { setResult(data); setStep("result"); toast.success("Import complete"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Import failed"),
  });

  const requiredUnmapped = useMemo(() => {
    if (!parsed) return [];
    return parsed.target_fields.filter((f) => f.required && !mapping[f.key]);
  }, [parsed, mapping]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) parseMut.mutate(f);
    e.target.value = "";
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="bulk-import-page">
      <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">Bulk Import</h1>
      <p className="text-sm text-slate-600 mt-1">Import Assets or Leads from an Excel/CSV file — same validation and rules as the manual forms.</p>

      {/* Mode toggle */}
      <div className="mt-5 inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="bulk-mode-toggle">
        {canAssets && (
          <button onClick={() => switchMode("assets")} className={`px-5 py-2 text-sm font-medium ${mode === "assets" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="bulk-mode-assets">Assets</button>
        )}
        {canLeads && (
          <button onClick={() => switchMode("leads")} className={`px-5 py-2 text-sm font-medium ${mode === "leads" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="bulk-mode-leads">Leads</button>
        )}
      </div>

      {/* Stepper */}
      <div className="mt-5 flex items-center gap-2 text-xs text-slate-500" data-testid="bulk-stepper">
        {["upload", "mapping", "preview", "result"].map((s, i) => (
          <React.Fragment key={s}>
            <span className={`px-2.5 py-1 rounded-full font-semibold capitalize ${step === s ? "bg-slate-900 text-white" : "bg-slate-100"}`}>{i + 1}. {s}</span>
            {i < 3 && <span className="text-slate-300">→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Upload */}
      {step === "upload" && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="bulk-upload-step">
          <Button variant="outline" onClick={() => downloadBlob(`/bulk-import/${mode}/template`, `bulk_import_${mode}_template.xlsx`)} data-testid="bulk-download-template">
            <Download className="h-4 w-4 mr-1.5" /> Download {mode === "assets" ? "Asset" : "Lead"} Template (.xlsx)
          </Button>
          <div className="mt-5 border-2 border-dashed border-slate-300 rounded-md p-10 text-center">
            <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-400" />
            <p className="text-sm text-slate-600 mt-2">Upload .xlsx or .csv (max 500 rows)</p>
            <label className="mt-3 inline-block cursor-pointer">
              <Button asChild disabled={parseMut.isPending} className="bg-slate-900 hover:bg-slate-800">
                <span>{parseMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Choose File</span>
              </Button>
              <input type="file" className="hidden" accept=".xlsx,.csv" onChange={handleFile} data-testid="bulk-file-input" />
            </label>
          </div>
        </div>
      )}

      {/* STEP 2: Mapping */}
      {step === "mapping" && parsed && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="bulk-mapping-step">
          <div className="text-sm text-slate-600 mb-4">Match your file's columns to the {mode} fields. Required fields are marked <span className="text-red-600">*</span>.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {parsed.target_fields.map((f) => (
              <div key={f.key} className="flex items-center gap-3" data-testid={`bulk-map-${f.key}`}>
                <div className="w-1/2 text-sm text-slate-800">{f.header}{f.required && <span className="text-red-600"> *</span>}</div>
                <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? "" : v }))}>
                  <SelectTrigger className="w-1/2 h-9 text-sm" data-testid={`bulk-map-select-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not mapped —</SelectItem>
                    {parsed.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {requiredUnmapped.length > 0 && (
            <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="bulk-required-warning">
              <AlertTriangle className="h-4 w-4 inline mr-1" /> Map required fields: {requiredUnmapped.map((f) => f.header).join(", ")}
            </div>
          )}
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={reset}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={requiredUnmapped.length > 0 || previewMut.isPending} onClick={() => previewMut.mutate()} data-testid="bulk-to-preview">
              {previewMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Preview &amp; Validate <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Preview */}
      {step === "preview" && previewData && (
        <div className="mt-6" data-testid="bulk-preview-step">
          <div className="flex flex-wrap gap-3 mb-4">
            <SummaryPill label="Will Create" value={previewData.summary.will_create} tone="emerald" />
            {mode === "leads" && <SummaryPill label="Will Update" value={previewData.summary.will_update} tone="blue" />}
            <SummaryPill label="New Taxonomy" value={previewData.summary.will_create_taxonomy} tone="amber" />
            <SummaryPill label="Errors" value={previewData.summary.error} tone="red" />
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr>
                <th className="label-eyebrow !text-slate-600">#</th>
                <th className="label-eyebrow !text-slate-600">Status</th>
                <th className="label-eyebrow !text-slate-600">Key Values</th>
                <th className="label-eyebrow !text-slate-600">Notes</th>
              </tr></thead>
              <tbody>
                {previewData.rows.map((r) => (
                  <tr key={r.row_index} className="border-t border-slate-100" data-testid={`bulk-preview-row-${r.row_index}`}>
                    <td className="text-sm text-slate-500 font-mono-num">{r.row_index + 1}</td>
                    <td><Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.status] || ""}`} data-testid={`bulk-row-status-${r.row_index}`}>{r.status}</Badge></td>
                    <td className="text-sm text-slate-700">{(r.raw[mode === "assets" ? "name" : "lead_name"] || "—")}{mode === "leads" ? ` · ${r.raw.contact_no || ""}` : ""}</td>
                    <td className="text-xs">
                      {r.errors.map((e, i) => <div key={i} className="text-red-600">{e}</div>)}
                      {r.new_values.map((n, i) => <div key={i} className="text-amber-700">+ new {n}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-between items-center">
            <Button variant="outline" onClick={() => setStep("mapping")}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Mapping</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={previewData.valid_count === 0 || commitMut.isPending} onClick={() => commitMut.mutate()} data-testid="bulk-commit">
              {commitMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Import {previewData.valid_count} row{previewData.valid_count === 1 ? "" : "s"}{previewData.error_count > 0 ? ` (${previewData.error_count} skipped)` : ""}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4: Result */}
      {step === "result" && result && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="bulk-result-step">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          <h2 className="font-display text-xl font-bold text-slate-900 mt-3">Import Complete</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <SummaryPill label="Created" value={result.created} tone="emerald" />
            {mode === "leads" && <SummaryPill label="Updated (existing)" value={result.updated} tone="blue" />}
            <SummaryPill label="Skipped (errors)" value={result.skipped} tone="amber" />
            {result.errored > 0 && <SummaryPill label="Failed to save" value={result.errored} tone="red" />}
          </div>
          <div className="mt-6 flex gap-3">
            {result.error_report_available && (
              <Button variant="outline" onClick={() => downloadBlob(`/bulk-import/error-report/${result.import_id}`, `bulk_import_errors.xlsx`)} data-testid="bulk-download-errors">
                <Download className="h-4 w-4 mr-1.5" /> Download Error Report
              </Button>
            )}
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={reset} data-testid="bulk-import-another">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Import Another File
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-800",
  };
  return (
    <div className={`px-4 py-2 rounded-md border ${tones[tone]}`}>
      <div className="text-2xl font-bold font-mono-num">{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide">{label}</div>
    </div>
  );
}
